import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Alert, Button } from 'reactstrap'
import { StandardPage, StandardForm, ApiService } from '../../shared'
import { useAuth } from 'auth/useAuth'
import { useUserGroups } from '../../shared/hooks/useUserGroups'
import { getMissingProfileFields } from '../../shared/utils/profileUtils'
import { canonicalCity } from '../../shared/services/ibgeService'
import { formatDateForFrontend, formatDateForBackend, validateDate } from './utils/dateUtils'
import { getValidationErrors } from './utils/validation'

/**
 * Legacy profiles stored "Rua X 123" in a single `address` field. Split only
 * when the last token looks like a house number; otherwise keep the whole
 * string as the street so "Rua das Flores" is never mangled.
 */
const splitLegacyAddress = (address) => {
  const s = (address || '').trim()
  if (!s) return { street: '', number: '' }
  const m = s.match(/^(.*\S)\s+(\d+[A-Za-z]?|s\/n|S\/N)$/)
  return m ? { street: m[1], number: m[2] } : { street: s, number: '' }
}

const Profile = () => {
  const auth = useAuth()
  const location = useLocation()
  const { isAdmin, isAssociate } = useUserGroups()
  const isGuest = !isAdmin && !isAssociate
  // Sent here by ProfileRequiredRoute? Explain what is missing, inline.
  const [gateNotice, setGateNotice] = useState(
    location.state?.reason === 'profile-required' ? (location.state.missing || []) : null
  )
  // After a save that completes the profile, point guests to the next step.
  const [nextStep, setNextStep] = useState(null)
  // Name/surname typed at sign-up live in Cognito (given_name/family_name)
  // and arrive in the ID token; use them to prefill a profile that doesn't
  // exist yet so the user is not asked for them twice.
  const claims = auth.user?.profile || {}
  const personalFields = [
    { name: 'name', label: 'Nome', type: 'text', placeholder: 'Digite seu nome', required: true, props: { autoComplete: 'given-name' } },
    { name: 'familyName', label: 'Sobrenome', type: 'text', placeholder: 'Digite seu sobrenome', required: true, props: { autoComplete: 'family-name' } },
    { name: 'dateOfBirth', label: 'Date of Birth', type: 'date-mask', placeholder: 'DD/MM/AAAA', props: { inputMode: 'numeric', autoComplete: 'bday' } },
    {
      name: 'gender', label: 'Gênero', type: 'select', placeholder: 'Selecione o gênero',
      options: [
        { value: 'masculino', label: 'Masculino' },
        { value: 'feminino', label: 'Feminino' },
        { value: 'outros', label: 'Outro' },
        { value: 'prefiro_nao_informar', label: 'Prefiro não informar' }
      ]
    },
    {
      name: 'estadoCivil', label: 'Estado Civil', type: 'select', placeholder: 'Selecione o estado civil',
      options: [
        { value: 'solteiro', label: 'Solteiro(a)' },
        { value: 'casado', label: 'Casado(a)' },
        { value: 'divorciado', label: 'Divorciado(a)' },
        { value: 'viuvo', label: 'Viúvo(a)' },
        { value: 'uniao_estavel', label: 'União Estável' }
      ]
    },
    { name: 'hasCNH', label: 'Possui CNH', type: 'checkbox', placeholder: 'Marque se possui CNH' }
  ]

  const addressFields = [
    { name: 'cep', label: 'CEP', type: 'cep', placeholder: '00000-000', props: { maxLength: 9 } },
    // UF and city are closed lists (IBGE) so every profile stores the same
    // canonical spelling — no more "GUAPIACU" vs "Guapiaçu".
    { name: 'state', label: 'State', type: 'uf-select', placeholder: 'Selecione o estado' },
    { name: 'city', label: 'City', type: 'city-select', placeholder: 'Busque a cidade' },
    { name: 'address1', label: 'Address', type: 'text', placeholder: 'Enter your address', props: { autoComplete: 'street-address' } },
    { name: 'addressNumber', label: 'Number', type: 'text', placeholder: 'Enter number', props: { inputMode: 'numeric' } },
    { name: 'addressComplement', label: 'Complement', type: 'text', placeholder: 'Enter complement' },
    { name: 'neighborhood', label: 'Bairro', type: 'text', placeholder: 'Digite o bairro' }
  ]

  const contactSocialFields = [
    { name: 'phone', label: 'Telefone', type: 'phone', placeholder: '(11) 1234-5678' },
    { name: 'cellphone', label: 'Celular', type: 'phone', placeholder: '(11) 91234-5678' },
    { name: 'facebookProfile', label: 'Facebook', type: 'text', placeholder: 'URL do perfil ou @usuario' },
    { name: 'instagramProfile', label: 'Instagram', type: 'text', placeholder: 'URL do perfil ou @usuario' },
    { name: 'linkedinProfile', label: 'LinkedIn', type: 'text', placeholder: 'URL do perfil LinkedIn' }
  ]

  const transformApiData = (userProfile) => {
    if (!userProfile) {
      return {
        name: claims.given_name || '',
        familyName: claims.family_name || '',
      }
    }
    return {
      name: userProfile.firstName || claims.given_name || '',
      familyName: userProfile.lastName || claims.family_name || '',
      dateOfBirth: userProfile.dateOfBirth ? formatDateForFrontend(userProfile.dateOfBirth) : '',
      gender: userProfile.gender || '',
      estadoCivil: userProfile.estadoCivil || '',
      hasCNH: userProfile.hasCNH || false,
      phone: userProfile.phone || '',
      cellphone: userProfile.cellphone || '',
      facebookProfile: userProfile.facebookProfile || '',
      instagramProfile: userProfile.instagramProfile || '',
      linkedinProfile: userProfile.linkedinProfile || '',
      cep: userProfile.zipCode || '',
      // New records carry `addressNumber`; older ones need the legacy split.
      ...(userProfile.addressNumber !== undefined && userProfile.addressNumber !== null
        ? { address1: userProfile.address || '', addressNumber: String(userProfile.addressNumber) }
        : (({ street, number }) => ({ address1: street, addressNumber: number }))(splitLegacyAddress(userProfile.address))),
      addressComplement: userProfile.addressComplement || '',
      neighborhood: userProfile.neighborhood || '',
      city: userProfile.city || '',
      state: userProfile.state || ''
    }
  }

  const transformFormData = (formData) => ({
    firstName: formData.name || '',
    lastName: formData.familyName || '',
    dateOfBirth: formData.dateOfBirth ? formatDateForBackend(formData.dateOfBirth) : null,
    gender: formData.gender || null,
    estadoCivil: formData.estadoCivil || null,
    hasCNH: formData.hasCNH || false,
    phone: formData.phone || null,
    cellphone: formData.cellphone || null,
    facebookProfile: formData.facebookProfile || null,
    instagramProfile: formData.instagramProfile || null,
    linkedinProfile: formData.linkedinProfile || null,
    address: (formData.address1 || '').trim(),
    addressNumber: (formData.addressNumber || '').trim(),
    addressComplement: formData.addressComplement,
    neighborhood: formData.neighborhood,
    city: formData.city,
    state: formData.state,
    zipCode: formData.cep
  })

  const validate = (values) => {
    const errors = getValidationErrors(values)
    if (!(values.name || '').trim()) errors.name = 'Informe seu nome.'
    if (!(values.familyName || '').trim()) errors.familyName = 'Informe seu sobrenome.'
    if (values.dateOfBirth && validateDate(values.dateOfBirth, (s) => s)) {
      errors.dateOfBirth = 'Data de nascimento inválida. Use DD/MM/AAAA.'
    }
    if (values.state && !values.city) errors.city = 'Selecione a cidade.'
    return errors
  }

  const handleAddressChange = async (addressData, setValues) => {
    // The CEP provider may return the city upper-cased/unaccented; snap it to
    // the IBGE spelling before it reaches the form.
    const state = (addressData.state || '').toUpperCase()
    let city = addressData.city
    if (city && state) {
      try {
        city = (await canonicalCity(city, state)) || city
      } catch { /* keep provider value; backend normalizes again on save */ }
    }
    setValues(prev => ({
      ...prev,
      ...addressData,
      state,
      city,
    }))
  }

  const handleSaved = (saved) => {
    const profile = saved?.data || saved
    if (!profile) return
    const missing = getMissingProfileFields(profile)
    setGateNotice(null)
    setNextStep(isGuest && missing.length === 0 ? 'curriculum' : (isGuest ? missing : null))
  }

  const renderForm = ({ values, errors, handleChange, setValues, t }) => (
    <>
      {gateNotice && (
        <Alert color="warning" className="d-flex align-items-start gap-2">
          <i className="bx bx-info-circle font-size-18" aria-hidden="true" />
          <div>
            <strong>Complete seu perfil para acessar o currículo.</strong>
            {gateNotice.length > 0 && <div className="small mt-1">Falta preencher: {gateNotice.join(', ')}.</div>}
          </div>
        </Alert>
      )}
      {nextStep === 'curriculum' && (
        <Alert color="success" className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <span><i className="bx bx-check-circle me-1" aria-hidden="true" />Perfil completo! Próximo passo: preencher seu currículo.</span>
          <Button tag={Link} to="/curriculum" color="success" size="sm">Preencher currículo <i className="bx bx-right-arrow-alt ms-1" aria-hidden="true" /></Button>
        </Alert>
      )}
      {Array.isArray(nextStep) && nextStep.length > 0 && (
        <Alert color="info" className="small">
          Perfil salvo. Para liberar o currículo, falta preencher: {nextStep.join(', ')}.
        </Alert>
      )}
      <StandardForm
        title="Profile Information"
        description="Please fill in your profile information below."
        fields={personalFields}
        values={values}
        errors={errors}
        handleChange={handleChange}
        t={t}
      />
      <StandardForm
        title="Address Information"
        description="Please fill in your address information below."
        fields={addressFields.map(field => {
          if (field.name === 'cep') return { ...field, onAddressChange: (data) => handleAddressChange(data, setValues) }
          if (field.name === 'city') return { ...field, props: { ...field.props, uf: values.state } }
          if (field.name === 'state') {
            // Changing the state invalidates the chosen city.
            return {
              ...field,
              onChange: (e) => setValues(prev => ({ ...prev, state: e.target.value, city: prev.state === e.target.value ? prev.city : '' })),
            }
          }
          return field
        })}
        values={values}
        errors={errors}
        handleChange={handleChange}
        t={t}
      />
      <StandardForm
        title="Contatos e Social"
        description="Preencha suas informações de contato e redes sociais."
        fields={contactSocialFields}
        values={values}
        errors={errors}
        handleChange={handleChange}
        t={t}
      />
    </>
  )

  return (
    <StandardPage
      title="Home"
      breadcrumbItem="My Profile"
      fetchData={ApiService.getUserProfile}
      submitData={(formData) => ApiService.updateUserProfile(transformFormData(formData))}
      transformData={transformApiData}
      validate={validate}
      onSaved={handleSaved}
      renderForm={renderForm}
    />
  )
}

export default Profile